/**
 * AWS Amplify Configuration — Cognito User Pool auth.
 *
 * This must be imported before the app renders (in index.jsx).
 * Amplify manages the ID/Access/Refresh tokens automatically,
 * including silent token refresh when the Access Token expires.
 */

import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';

const isDev = import.meta.env.VITE_DEVMODE === 'true';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: isDev
        ? import.meta.env.VITE_DEV_COGNITO_POOL_ID
        : import.meta.env.VITE_COGNITO_POOL_ID,
      userPoolClientId: isDev
        ? import.meta.env.VITE_DEV_COGNITO_CLIENT_ID
        : import.meta.env.VITE_COGNITO_CLIENT_ID,
    },
  },
});

/**
 * Keep Cognito's tokens in sessionStorage, not localStorage.
 *
 * MUST run after Amplify.configure(): configure() installs the default token
 * provider backed by `defaultStorage`, which is localStorage
 * (DefaultStorage -> getLocalStorageWithFallback in @aws-amplify/core), and this
 * call re-points that provider's storage.
 *
 * Why it matters. localStorage is shared by every tab in the browser profile
 * while the portal's own session blob lives in sessionStorage, which is per-tab.
 * A second sign-in anywhere in the browser replaced the shared token and left
 * every other open tab sending the NEW user's JWT while still showing the OLD
 * user's permissions and sending the old user's id in the request body. That is
 * exactly how a weight-20 test account came to submit a campus_add that the UI
 * had authorised against a weight-1000 session.
 *
 * With both stores tab-scoped, that divergence cannot be created in the first
 * place. The identity guard in services/auth.js is the second line of defence
 * for the cases this cannot reach (a token swapped inside one tab).
 *
 * Trade-off, accepted deliberately: no cross-tab single sign-on. Opening the
 * portal in a second tab requires signing in again, and closing a tab ends that
 * tab's session. Sessions were already effectively one-login-one-tab because the
 * session blob is sessionStorage-bound, so this aligns the token with the
 * behaviour the app already had.
 *
 * Deploy note: existing users hold tokens in localStorage, which this build no
 * longer reads. Everyone is signed out once on rollout and signs back in.
 */
cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);

/**
 * One-time cleanup that belongs to the storage move above.
 *
 * Everyone who signed in on a previous build has a full Cognito token set in
 * localStorage — id, access, and the long-lived refresh token. This build never
 * reads those keys again, so they are orphaned credentials sitting at rest in a
 * store that does not expire. Only the browser can remove them: no deploy step or
 * server-side action can reach another machine's localStorage. So the app clears
 * them itself on first boot after the upgrade.
 *
 * localStorage ONLY. The tokens this build writes live in sessionStorage under the
 * same `CognitoIdentityServiceProvider` prefix, so purging that store would sign
 * the user out on every page load.
 *
 * Keys are collected before removal: removeItem() re-indexes the store, so
 * deleting while walking by index skips entries.
 */
try {
  const AUTH_KEY_PREFIX = 'CognitoIdentityServiceProvider';
  const stale = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (key && key.startsWith(AUTH_KEY_PREFIX)) stale.push(key);
  }
  stale.forEach((key) => window.localStorage.removeItem(key));
  if (stale.length) {
    console.info(`[auth] Cleared ${stale.length} orphaned Cognito token entries from localStorage.`);
  }
} catch {
  /* Storage access throws in some private-browsing modes. A failed purge must
     never stop the app from booting. */
}
